/**
 * eSIM-related click handlers — open-topup (header CTA with picker),
 * add-new-esim (+ Add tile, always create), get-esim (country-page CTA).
 *
 * Each handler enforces:
 *   - Auth gate (open-login if unauth'd)
 *   - Single-reservation invariant via `assertNoActiveReservation` (helper
 *     bounces to /dashboard/esims with toast if a reserved eSIM exists)
 *
 * Originally inline in `main.ts` lines 117-244. Phase 2.3 split 2026-05-12.
 */
import { Auth } from '../auth';
import { withLocale } from '../i18n';
import { openAuth, openTopUp } from '../modals';
import { assertNoActiveReservation } from '../reservation-guard';
import { haptic as tgHaptic } from '../telegram-mini-app';

/**
 * Header "Top up" CTA — ALWAYS opens the picker so the user explicitly
 * chooses between existing eSIMs vs creating a new one. Operator pass
 * 2026-05-10 ночь +26: «там должно быть выбор, между текущей/текущими
 * есимами и новой».
 *
 * Earlier logic auto-routed (1 active → direct topup, ≥2 → picker, none →
 * create). That auto-routing was the source of confusion: with 1 existing
 * eSIM + a reserved one already pending, header CTA blocked entirely
 * because of the reservation guard, and user couldn't even top up the
 * existing line. Now the picker is the single entry point for the header.
 *
 * Special case: brand-new user with 0 eSIMs and 0 reservation → skip
 * picker (nothing to choose between) and go straight to create.
 */
export function openTopupAction(e: MouseEvent, target: HTMLElement): void {
  e.preventDefault();
  tgHaptic.tap();
  if (!Auth.isAuthed()) {
    openAuth({ redirectTo: '/dashboard/esims' });
    return;
  }
  // Optional informational country slug from a country-page CTA.
  const countrySlug = target.dataset.countrySlug;
  const countryName = target.dataset.countryName;
  const countryFlag = target.dataset.countryFlag;
  void (async () => {
    try {
      const { fetchEsims } = await import('../api');
      const { esims } = await fetchEsims();
      const visible = esims.filter((e) => e.status !== 'archived' && e.status !== 'released');
      // Top-up-able = active or paused. RESERVED eSIMs are NOT toppable —
      // they need to be paid for first. We surface `reserved` separately
      // so the picker can disable the "New eSIM" option with a hint.
      const topUpable = visible.filter((e) => e.status === 'active' || e.status === 'paused');
      const reserved = visible.find((e) => e.status === 'reserved') ?? null;
      const onDashboardEsims = window.location.pathname.endsWith('/dashboard/esims');

      // Brand-new user with nothing on file → skip picker (no choice to make).
      if (topUpable.length === 0 && !reserved) {
        openTopUp({
          kind: 'create',
          ...(countrySlug ? { countrySlug } : {}),
          ...(countryName ? { countryName } : {}),
          ...(countryFlag ? { flag: countryFlag } : {}),
        });
        return;
      }

      // Off-dashboard with state → navigate to dashboard so the picker
      // appears on top of the user's eSIM grid (visual context).
      if (!onDashboardEsims) {
        window.history.pushState({}, '', withLocale('/dashboard/esims'));
        window.dispatchEvent(new PopStateEvent('popstate'));
        window.requestAnimationFrame(async () => {
          const { openEsimPicker } = await import('../modals/esim-picker');
          openEsimPicker(topUpable, { reserved });
        });
        return;
      }

      // On-dashboard — show picker directly.
      const { openEsimPicker } = await import('../modals/esim-picker');
      openEsimPicker(topUpable, { reserved });
    } catch {
      // Fallback: just send them to the dashboard.
      window.history.pushState({}, '', withLocale('/dashboard/esims'));
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  })();
}

/**
 * Explicit "+ Add eSIM" tile on /dashboard/esims. Always opens the CREATE
 * flow — never falls through to topup-existing. Distinct from `open-topup`
 * (header CTA) which is intentionally ambiguous and may route to topup-
 * an-existing-eSIM when there's a clear single target.
 *
 * Operator-discovered 2026-05-10 ночь +25: shared action was running
 * 4 TOPUP intents against the same eSIM instead of creating new ones.
 */
export function addNewEsimAction(e: MouseEvent): void {
  e.preventDefault();
  tgHaptic.tap();
  if (!Auth.isAuthed()) {
    openAuth({ redirectTo: '/dashboard/esims' });
    return;
  }
  void (async () => {
    // Single-reservation invariant (operator-set 2026-05-11). Bails with a
    // "Pay your existing reservation first" toast + nav to /dashboard/esims
    // if any reserved eSIM is found.
    if (!(await assertNoActiveReservation())) return;
    openTopUp({ kind: 'create' });
  })();
}

/**
 * Country-page Top-up CTA. The slug is informational (eSIM is global) but
 * we keep stashing it so we can show "you came from X" as a chip in the
 * create modal, both before and after sign-in.
 *
 * Single-reservation invariant (operator-set 2026-05-11): country-page CTA
 * used to be the missing guard — user could reserve on /countries/fr,
 * then /countries/de, then /countries/it before paying any. Now bails to
 * the dashboard's Pay-now card if any reserved eSIM is found.
 */
export function getEsimAction(e: MouseEvent, target: HTMLElement): void {
  e.preventDefault();
  // Primary-action haptic; tap before any async work so user feels the
  // intent register immediately.
  tgHaptic.tap();
  const slug = target.dataset.countrySlug;
  if (!slug) return;
  const countryName = target.dataset.countryName;
  const flag = target.dataset.countryFlag;
  if (!Auth.isAuthed()) {
    sessionStorage.setItem('roamzy:pending-esim-slug', JSON.stringify({ slug, ts: Date.now() }));
    document.dispatchEvent(new CustomEvent('roamzy:open-login', { detail: { redirectTo: '/dashboard/esims' } }));
    return;
  }
  void (async () => {
    if (!(await assertNoActiveReservation())) return;
    openTopUp({
      kind: 'create',
      countrySlug: slug,
      ...(countryName ? { countryName } : {}),
      ...(flag ? { flag } : {}),
    });
  })();
}
